/**
 * useProspectVideos Hook
 * Manages prospect video state with signed URL caching
 * Implements 15-minute expiry buffer strategy (matches mobile)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as prospectMediaService from '../services/prospect-media';
import type { CachedSignedUrl, ProspectVideo, VideoFilters } from '../types/prospect-media';
import { useDebounce } from './useDebounce';

const EXPIRY_BUFFER_MINUTES = 15;

/** Typing pause before a search re-queries. Matches the org-wide list. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * @param filters Narrows the gallery server-side. Omit for the whole gallery.
 */
export function useProspectVideos(prospectId: string, filters?: VideoFilters) {
  const [videos, setVideos] = useState<ProspectVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [videoUrlCache, setVideoUrlCache] = useState<Map<string, CachedSignedUrl>>(new Map());
  const [thumbnailUrlCache, setThumbnailUrlCache] = useState<Map<string, CachedSignedUrl>>(new Map());
  const [refreshing, setRefreshing] = useState(false);

  // Only the search box is debounced; the tag pills change one click at a time and
  // waiting on those reads as lag rather than as batching.
  const debouncedSearchTerm = useDebounce(filters?.searchTerm ?? '', SEARCH_DEBOUNCE_MS);

  // Depend on the joined ids, not the array — callers build a fresh filters object every
  // render, and keying off identity would refetch forever.
  const tagIdsKey = (filters?.tagIds ?? []).join(',');
  const uploadedBy = filters?.uploadedBy ?? null;
  const uploadedAfter = filters?.uploadedAfter ?? null;
  const uploadedBefore = filters?.uploadedBefore ?? null;

  const requestFilters = useMemo(
    () => ({
      searchTerm: debouncedSearchTerm,
      tagIds: tagIdsKey ? tagIdsKey.split(',') : [],
      uploadedBy,
      uploadedAfter,
      uploadedBefore,
    }),
    [debouncedSearchTerm, tagIdsKey, uploadedBy, uploadedAfter, uploadedBefore]
  );

  // The filters that produced the list currently on screen. A signed-URL refresh has to
  // re-query with THESE, not with whatever the user has since selected — otherwise a tile
  // that is still painted and still clickable can be absent from the response that is
  // supposed to renew its URL, and the click dies with nothing but a console error.
  const paintedFilters = useRef(requestFilters);

  // Responses can land out of order: the deps effect, and one getThumbnailUrl per tile,
  // can all be in flight at once. Only the newest may write, or an older unfiltered
  // response paints the full list while the filter pills read as selected.
  const latestRequest = useRef(0);

  const runFetch = useCallback(
    async (activeFilters: typeof requestFilters) => {
      const requestId = latestRequest.current + 1;
      latestRequest.current = requestId;

      try {
        setLoading(true);
        setError(null);
        const fetchedVideos = await prospectMediaService.getVideos(prospectId, activeFilters);
        if (latestRequest.current !== requestId) return fetchedVideos;
        paintedFilters.current = activeFilters;
        setVideos(fetchedVideos);
        return fetchedVideos;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        console.error('[ProspectVideos] Failed to fetch videos', {
          error: err,
          prospectId,
        });
        if (latestRequest.current === requestId) setError(error);
        throw error;
      } finally {
        if (latestRequest.current === requestId) setLoading(false);
      }
    },
    [prospectId]
  );

  // Fetch videos from API
  const fetchVideos = useCallback(
    () => runFetch(requestFilters),
    [runFetch, requestFilters]
  );

  /** Re-query the list the user is looking at, to renew its signed URLs. */
  const refetchPainted = useCallback(
    () => runFetch(paintedFilters.current),
    [runFetch]
  );

  // Update cache when videos change
  useEffect(() => {
    const newVideoCache = new Map<string, CachedSignedUrl>();
    const newThumbnailCache = new Map<string, CachedSignedUrl>();

    videos.forEach((video) => {
      newVideoCache.set(video.id, {
        signedUrl: video.gcsUrl,
        expiresAt: new Date(video.urlExpiresAt),
      });

      if (video.thumbnailGcsUrl && video.thumbnailUrlExpiresAt) {
        newThumbnailCache.set(video.id, {
          signedUrl: video.thumbnailGcsUrl,
          expiresAt: new Date(video.thumbnailUrlExpiresAt),
        });
      }
    });

    setVideoUrlCache(newVideoCache);
    setThumbnailUrlCache(newThumbnailCache);
  }, [videos]);

  // Check if URLs need refresh (15-minute buffer)
  const needsRefresh = useMemo(() => {
    if (videoUrlCache.size === 0) return false;

    const now = Date.now();
    const bufferMs = EXPIRY_BUFFER_MINUTES * 60 * 1000;

    for (const cached of videoUrlCache.values()) {
      const timeUntilExpiry = cached.expiresAt.getTime() - now;
      if (timeUntilExpiry < bufferMs) {
        return true;
      }
    }

    return false;
  }, [videoUrlCache]);

  // Get video URL with expiry check
  const getVideoUrl = useCallback(
    async (videoId: string): Promise<string | null> => {
      const cached = videoUrlCache.get(videoId);

      // If expired or not cached, refresh
      if (!cached || cached.expiresAt.getTime() < Date.now()) {
        setRefreshing(true);
        try {
          // The list the tile came from, not the filters the user has since picked.
          const fetchedVideos = await refetchPainted();
          const video = fetchedVideos.find((v) => v.id === videoId);
          if (!video) {
            console.error('[ProspectVideos] Video URL not found after refresh', {
              videoId,
              prospectId,
            });
            return null;
          }
          return video.gcsUrl;
        } catch (error) {
          console.error('[ProspectVideos] Failed to refresh video URLs', {
            error,
            videoId,
            prospectId,
          });
          return null;
        } finally {
          setRefreshing(false);
        }
      }

      return cached.signedUrl;
    },
    [videoUrlCache, refetchPainted, prospectId]
  );

  // Get thumbnail URL with expiry check
  const getThumbnailUrl = useCallback(
    async (videoId: string): Promise<string | null> => {
      const cached = thumbnailUrlCache.get(videoId);

      // If expired or not cached, refresh
      if (!cached || cached.expiresAt.getTime() < Date.now()) {
        setRefreshing(true);
        try {
          const fetchedVideos = await refetchPainted();
          const video = fetchedVideos.find((v) => v.id === videoId);
          if (!video || !video.thumbnailGcsUrl) {
            console.error('[ProspectVideos] Thumbnail URL not found after refresh', {
              videoId,
              prospectId,
            });
            return null;
          }
          return video.thumbnailGcsUrl;
        } catch (error) {
          console.error('[ProspectVideos] Failed to refresh thumbnail URLs', {
            error,
            videoId,
            prospectId,
          });
          return null;
        } finally {
          setRefreshing(false);
        }
      }

      return cached.signedUrl;
    },
    [thumbnailUrlCache, refetchPainted, prospectId]
  );

  // Delete video with optimistic update
  const deleteVideo = useCallback(
    async (videoId: string) => {
      // Optimistic update
      const previousVideos = videos;
      setVideos((prev) => prev.filter((v) => v.id !== videoId));

      try {
        await prospectMediaService.deleteVideo(prospectId, videoId);
      } catch (err) {
        console.error('[ProspectVideos] Failed to delete video', {
          error: err,
          prospectId,
          videoId,
        });
        // Revert on error
        setVideos(previousVideos);
        throw err;
      }
    },
    [prospectId, videos]
  );

  // Initial fetch, and a refetch on every filter change.
  useEffect(() => {
    // Deliberately swallowed: fetchVideos rethrows so that callers awaiting `refresh`
    // (e.g. after an upload) can report the failure, but here the `error` state already
    // carries it — and this now runs per keystroke, so a bare call would turn a flaky
    // endpoint into a stream of unhandled rejections.
    fetchVideos().catch(() => undefined);
  }, [fetchVideos]);

  return {
    videos,
    loading,
    error,
    refresh: fetchVideos,
    deleteVideo,
    getVideoUrl,
    getThumbnailUrl,
    needsRefresh,
    refreshing,
  };
}
